import { z } from 'zod';
import { bereichSchema, commaSeparatedIds, DEFAULT_BEREICH, normalizeForSearch, } from './documents.js';
import { blockText, docOfValue, isPlainDoc, normalizeDoc, normalizeRich, parseRichJson, serializeDoc, singleLineBlock, toRichValue, trimBlock, } from './richtext.js';
/**
 * Die Abteilungen, mit denen eine frische Einkaufsliste beginnt – in der
 * Reihenfolge, in der man sie üblicherweise abläuft. Die Liste sortiert
 * danach, damit man nicht zweimal durch denselben Gang muss.
 *
 * Seit sich Abteilungen in der App anlegen, umbenennen und umordnen lassen,
 * ist diese Liste nicht mehr die Wahrheit, sondern nur noch der Anfang: Die
 * Datenbank sagt, welche Abteilungen dieser Haushalt hat und in welcher
 * Reihenfolge sie stehen. Hier steht, womit er startet – und woran die
 * Stichwortliste unten ihre Erstzuordnung hängt.
 */
export const STORE_SECTIONS = [
    'Früchte & Gemüse',
    'Brot & Backwaren',
    'Molkerei',
    'Fleisch & Fisch',
    'Vorrat',
    'Tiefkühl',
    'Getränke',
    'Haushalt',
    'Sonstiges',
];
/**
 * Erstzuordnung für einen neuen Eintrag, wenn er noch nie auf der Liste stand.
 *
 * Bewusst eine schlichte Stichwortliste statt einer klugen Erkennung: Wird sie
 * einmal korrigiert, merkt sich die App die Zuordnung ohnehin dauerhaft. Sie
 * muss also nur oft genug richtig liegen, um Arbeit zu sparen.
 */
export const SECTION_KEYWORDS = {
    apfel: 'Früchte & Gemüse',
    banane: 'Früchte & Gemüse',
    salat: 'Früchte & Gemüse',
    tomate: 'Früchte & Gemüse',
    gurke: 'Früchte & Gemüse',
    zwiebel: 'Früchte & Gemüse',
    kartoffel: 'Früchte & Gemüse',
    karotte: 'Früchte & Gemüse',
    // „Rüebli“ wird zu „rueebli“ ausgeschrieben, „Rübli“ zu „ruebli“ – beide
    // Schreibweisen sind hier gebräuchlich, also stehen beide in der Liste.
    rueebli: 'Früchte & Gemüse',
    ruebli: 'Früchte & Gemüse',
    zucchetti: 'Früchte & Gemüse',
    peperoni: 'Früchte & Gemüse',
    zitrone: 'Früchte & Gemüse',
    beeren: 'Früchte & Gemüse',
    obst: 'Früchte & Gemüse',
    gemuese: 'Früchte & Gemüse',
    brot: 'Brot & Backwaren',
    broetchen: 'Brot & Backwaren',
    gipfeli: 'Brot & Backwaren',
    zopf: 'Brot & Backwaren',
    kuchen: 'Brot & Backwaren',
    milch: 'Molkerei',
    butter: 'Molkerei',
    kaese: 'Molkerei',
    joghurt: 'Molkerei',
    rahm: 'Molkerei',
    quark: 'Molkerei',
    ei: 'Molkerei',
    eier: 'Molkerei',
    fleisch: 'Fleisch & Fisch',
    poulet: 'Fleisch & Fisch',
    hackfleisch: 'Fleisch & Fisch',
    wurst: 'Fleisch & Fisch',
    schinken: 'Fleisch & Fisch',
    lachs: 'Fleisch & Fisch',
    fisch: 'Fleisch & Fisch',
    reis: 'Vorrat',
    teigwaren: 'Vorrat',
    pasta: 'Vorrat',
    mehl: 'Vorrat',
    zucker: 'Vorrat',
    salz: 'Vorrat',
    oel: 'Vorrat',
    essig: 'Vorrat',
    konserve: 'Vorrat',
    mueesli: 'Vorrat',
    kaffee: 'Vorrat',
    tee: 'Vorrat',
    glace: 'Tiefkühl',
    pizza: 'Tiefkühl',
    tiefkuehl: 'Tiefkühl',
    wasser: 'Getränke',
    saft: 'Getränke',
    bier: 'Getränke',
    wein: 'Getränke',
    sirup: 'Getränke',
    waschmittel: 'Haushalt',
    abwaschmittel: 'Haushalt',
    putzmittel: 'Haushalt',
    klopapier: 'Haushalt',
    wc: 'Haushalt',
    haushaltpapier: 'Haushalt',
    zahnpasta: 'Haushalt',
    shampoo: 'Haushalt',
    seife: 'Haushalt',
    batterien: 'Haushalt',
};
export function guessSection(text) {
    const normalized = normalizeForSearch(text);
    for (const word of normalized.split(' ')) {
        const hit = SECTION_KEYWORDS[word];
        if (hit)
            return hit;
    }
    // Auch Teilwörter prüfen: „Vollmilch" soll bei „milch" landen.
    for (const [keyword, section] of Object.entries(SECTION_KEYWORDS)) {
        if (keyword.length >= 4 && normalized.includes(keyword))
            return section;
    }
    return 'Sonstiges';
}
export const storeSectionSchema = z.enum(STORE_SECTIONS);
/**
 * Eine Abteilung, wie sie in der Datenbank steht.
 *
 * Ein Eintrag zeigt mit `sectionId` darauf und nicht auf den Namen: Wer
 * „Molkerei" in „Kühlregal" umbenennt, meint dieselbe Abteilung – und alles,
 * was darin liegt, soll darin bleiben.
 */
export const shoppingSectionSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Kleiner heisst weiter vorn im Laden. */
    sortOrder: z.number(),
});
const sectionNameSchema = z
    .string()
    .trim()
    .min(1, 'Bitte einen Namen eingeben')
    .max(40, 'Zu lang');
export const createShoppingSectionSchema = z.object({ name: sectionNameSchema });
export const updateShoppingSectionSchema = z.object({ name: sectionNameSchema });
/**
 * Die neue Reihenfolge, als ganze Liste von Kennungen.
 *
 * Bewusst nicht „schieb diese eine Abteilung um einen Platz": Wer zweimal
 * schnell hintereinander tippt, schickt sonst zwei Anweisungen, die beide vom
 * alten Stand ausgehen – und die Liste steht danach anders da, als sie auf dem
 * Bildschirm aussah.
 */
export const reorderShoppingSectionsSchema = z.object({
    ids: z.array(z.string()).min(1, 'Keine Abteilungen angegeben'),
});
/**
 * Zwei Abteilungen gelten als dieselbe, wenn sie sich nur in Gross- und
 * Kleinschreibung, Umlautschreibweise oder Leerzeichen unterscheiden.
 *
 * „Getränke" und „getraenke" wären zwei Zeilen in der Datenbank, in der
 * Auswahl beim Einsortieren aber zweimal derselbe Knopf – und man träfe nie
 * verlässlich denselben.
 */
export function sameSectionName(a, b) {
    return normalizeForSearch(a) === normalizeForSearch(b);
}
/**
 * Die Abteilung, in der landet, was sich nicht zuordnen lässt – „Sonstiges"
 * aus der Erstausstattung.
 *
 * Kein Merkmal an der Zeile, sondern eine feste Kennung: Ein Merkmal müsste
 * beim Löschen weitergereicht werden, und dann gäbe es Zustände ohne
 * Sammelposten.
 */
export const CATCH_ALL_SECTION_ID = 'sonstiges';
/**
 * Wohin ein Eintrag kommt, für den keine Abteilung feststeht – und wohin die
 * Einträge einer gelöschten Abteilung wandern.
 *
 * `exclude` ist die Abteilung, die gerade verschwindet: Sie darf das Ziel
 * nicht sein. Wurde der Sammelposten selbst gelöscht, ist es die letzte
 * Abteilung – dort steht ein Sammelposten in einem Laden ohnehin. Gibt es
 * überhaupt keine andere, ist die Antwort `undefined`; dann verhindert der
 * Server das Löschen, statt Einträge ins Leere zu schieben.
 */
export function catchAllSection(sections, exclude) {
    const uebrig = sections.filter((section) => section.id !== exclude);
    return uebrig.find((section) => section.id === CATCH_ALL_SECTION_ID) ?? uebrig[uebrig.length - 1];
}
/**
 * Der Abstand zwischen zwei Abteilungen in der Sortierung. Die Lücken sind
 * Absicht: Eine neue Abteilung hängt sich hinten an, ohne dass eine einzige
 * bestehende Zeile angefasst werden muss.
 */
export const SECTION_SORT_STEP = 10;
/** Ganz hinten, mit Abstand zur letzten Abteilung. */
export function nextSectionSortOrder(sections) {
    const letzte = sections[sections.length - 1];
    return letzte ? letzte.sortOrder + SECTION_SORT_STEP : SECTION_SORT_STEP;
}
export const shoppingItemSchema = z.object({
    id: z.string(),
    text: z.string(),
    sectionId: z.string(),
    /**
     * Der Name der Abteilung, nur für eine App, die noch von vor den eigenen
     * Abteilungen stammt und danach gruppiert – siehe
     * `apps/api/src/lib/shopping-compat.ts`. Die heutige Oberfläche liest
     * `sectionId`; wer hier etwas anzeigt, zeigt einen Namen an, der nach dem
     * Umbenennen einer Abteilung nicht mehr stimmt.
     */
    section: storeSectionSchema.optional(),
    done: z.boolean(),
    createdBy: z.string(),
    doneBy: z.string().nullable(),
    createdAt: z.string(),
    doneAt: z.string().nullable(),
});
/**
 * Eine Kennung, die das Gerät selbst vergibt.
 *
 * Wer ohne Verbindung etwas anlegt, braucht sofort eine Kennung – für den
 * nächsten Haken, das Umbenennen, das Löschen, lange bevor der Server davon
 * weiss. Und wird die Anfrage später wiederholt, weil die erste Antwort im
 * Funkloch verloren ging, legt dieselbe Kennung nichts doppelt an. Fehlt sie
 * (eine ältere App), vergibt der Server eine.
 */
export const clientIdSchema = z.string().uuid();
export const createShoppingItemSchema = z.object({
    id: clientIdSchema.optional(),
    text: z.string().trim().min(1, 'Bitte etwas eingeben').max(120, 'Zu lang'),
    sectionId: z.string().optional(),
});
export const updateShoppingItemSchema = z.object({
    text: z.string().trim().min(1).max(120).optional(),
    sectionId: z.string().optional(),
    done: z.boolean().optional(),
});
/**
 * „Aufräumen" – auf Wunsch nur bestimmte Einträge.
 *
 * Kommt der Griff aus einer Warteschlange, die eine Weile ohne Verbindung
 * war, hat die andere Person in der Zwischenzeit vielleicht weiter abgehakt.
 * Weggeräumt wird dann nur, was hier erledigt dastand, als aufgeräumt wurde –
 * nicht das, was sie eben in den Wagen gelegt hat.
 */
export const clearDoneShoppingSchema = z.object({
    ids: z.array(z.string().max(60)).max(2000).optional(),
});
/**
 * Gelöschte Einträge zurückholen – „Rückgängig" nach dem Löschen oder
 * Aufräumen.
 *
 * Zurück kommt der Eintrag, wie er war: mit seiner Kennung, seinem Platz in
 * der Abteilung (der hängt am Zeitpunkt des Anlegens) und mit seinem Haken.
 * Ein neu angelegter Eintrag stünde sonst zuunterst und wäre wieder offen.
 */
export const restoreShoppingItemsSchema = z.object({
    items: z
        .array(z.object({
        id: z.string().min(1).max(60),
        text: z.string().trim().min(1).max(120),
        sectionId: z.string().max(60),
        done: z.boolean(),
        createdBy: z.string().max(60),
        doneBy: z.string().max(60).nullable(),
        createdAt: z.string().max(40),
        doneAt: z.string().max(40).nullable(),
    }))
        .min(1)
        .max(500),
});
/** Farben für Notizen – gedeckt gehalten, damit die Liste ruhig bleibt. */
export const NOTE_COLORS = ['default', 'gelb', 'gruen', 'blau', 'rosa'];
export const NOTE_COLOR_LABELS = {
    default: 'Ohne Farbe',
    gelb: 'Gelb',
    gruen: 'Grün',
    blau: 'Blau',
    rosa: 'Rosa',
};
export const noteColorSchema = z.enum(NOTE_COLORS);
/**
 * Zwei Arten von Notiz: Fliesstext oder eine Liste zum Abhaken.
 *
 * Beide liegen im selben Feld `body`. Eine Checkliste schreibt je Zeile einen
 * Eintrag, dem ein `[ ]` oder `[x]` vorangeht – dieselbe Schreibweise, die
 * Menschen ohnehin verwenden. Eine eigene Tabelle für Listeneinträge wäre die
 * lehrbuchmässige Lösung und hier reiner Ballast: Es gibt kein Sortieren über
 * Notizen hinweg, keine Rechte je Eintrag, und eine Notiz wird immer als
 * Ganzes gespeichert. Dafür bleibt die Notiz in der Datenbank lesbar, und die
 * Volltextsuche findet sie ohne Zusatzarbeit.
 */
export const NOTE_KINDS = ['text', 'liste'];
export const noteKindSchema = z.enum(NOTE_KINDS);
export const NOTE_KIND_LABELS = {
    text: 'Text',
    liste: 'Checkliste',
};
/**
 * Findet Verweise: `https://…`, `http://…`, `www.…` und E-Mail-Adressen.
 *
 * Bewusst diese vier und nicht „alles, was nach einer Domain aussieht": Ein
 * Satz wie „Das kostet 12.50 pro Person" enthält keinen Verweis, und ein
 * Muster, das ihn dafür hält, macht aus jeder Notiz ein Minenfeld.
 */
const LINK_PATTERN = /((?:https?:\/\/|www\.)[^\s<>"]+|[^\s<>"@]+@[^\s<>"@]+\.[a-zA-Z]{2,})/g;
/**
 * Zeichen, die am Ende eines Verweises fast nie dazugehören.
 *
 * „Siehe https://sbb.ch." endet mit einem Punkt, der zum Satz gehört und nicht
 * zur Adresse. Eine schliessende Klammer bleibt nur stehen, wenn im Verweis
 * auch eine öffnende steht – Wikipedia-Adressen leben davon.
 */
function trimTrailing(raw) {
    let link = raw;
    while (link.length > 0) {
        const last = link[link.length - 1];
        if ('.,;:!?«»"\''.includes(last)) {
            link = link.slice(0, -1);
            continue;
        }
        if (last === ')' && !link.includes('(')) {
            link = link.slice(0, -1);
            continue;
        }
        break;
    }
    return { link, rest: raw.slice(link.length) };
}
/**
 * Zerlegt einen Text in Stücke, damit die Oberfläche Verweise anklickbar
 * machen kann.
 *
 * Die Erkennung steht hier und nicht im Frontend: Sie gehört zum Verständnis
 * dessen, was in einer Notiz steht, und ist damit prüfbar, ohne einen Browser
 * zu starten.
 */
export function splitLinks(text) {
    const parts = [];
    let position = 0;
    for (const match of text.matchAll(LINK_PATTERN)) {
        const start = match.index ?? 0;
        const { link, rest } = trimTrailing(match[0]);
        if (link === '')
            continue;
        if (start > position)
            parts.push({ text: text.slice(position, start) });
        parts.push({ text: link, href: hrefFor(link) });
        if (rest)
            parts.push({ text: rest });
        position = start + match[0].length;
    }
    if (position < text.length)
        parts.push({ text: text.slice(position) });
    return parts;
}
/** Die Adresse, die hinter einem gefundenen Stück steckt. */
function hrefFor(link) {
    if (/^https?:\/\//i.test(link))
        return link;
    if (link.includes('@'))
        return `mailto:${link}`;
    return `https://${link}`;
}
/** `[x] Milch`, `[ ] Brot` – auch mit vorangestelltem Listenstrich. */
const CHECKLIST_LINE = /^\s*(?:[-*]\s*)?\[([ xX])\]\s?(.*)$/;
/**
 * Liest die Einträge einer Checkliste.
 *
 * Zeilen ohne Kästchen werden zu offenen Einträgen. Das ist der Fall, wenn
 * aus einer Textnotiz eine Liste wird oder jemand etwas hineinkopiert – beides
 * soll Einträge ergeben und keine Fehlermeldung.
 */
export function parseChecklist(body) {
    const items = [];
    for (const line of body.split('\n')) {
        const match = CHECKLIST_LINE.exec(line);
        const text = (match ? (match[2] ?? '') : line).trim();
        if (!text)
            continue;
        items.push({ text, done: match ? (match[1] ?? ' ').toLowerCase() === 'x' : false });
    }
    return items;
}
/** Schreibt die Einträge zurück. Leere fallen weg – sie sind eine Zeile im Entstehen. */
export function serializeChecklist(items) {
    return items
        .filter((item) => item.text.trim() !== '')
        .map((item) => `[${item.done ? 'x' : ' '}] ${item.text.trim()}`)
        .join('\n');
}
/**
 * Erledigtes nach unten.
 *
 * Eine Liste beantwortet die Frage „was ist noch zu tun" – und die steht dann
 * oben, ohne dass man zwischen Durchgestrichenem suchen muss. Die Reihenfolge
 * innerhalb der beiden Gruppen bleibt erhalten: Wer sich beim Schreiben etwas
 * dabei gedacht hat, findet es wieder.
 *
 * Sortiert wird der gespeicherte Inhalt selbst, nicht nur die Anzeige. So
 * sieht die Liste auf jedem Gerät gleich aus, und die Vorschau in der
 * Übersicht zeigt dieselben offenen Punkte wie die geöffnete Notiz.
 */
export function sortChecklist(items) {
    return [...items.filter((item) => !item.done), ...items.filter((item) => item.done)];
}
/**
 * Die Formatierung einer Checkliste – oder `null`, wenn sie nicht (mehr) passt.
 *
 * Eine Checkliste formatiert ihre Einträge, nicht ihre Zeilen samt Kästchen:
 * Im Formatfeld steht je Eintrag ein Absatz, ohne `[ ]` und ohne `[x]` – die
 * gehören dem gespeicherten Text. Gilt nur, wenn Absatz für Absatz derselbe
 * Wortlaut dasteht wie in den Einträgen des Textes. Hat eine ältere App die
 * Liste geändert, passt das nicht mehr, und die Liste steht unformatiert da –
 * dieselbe Regel wie beim Fliesstext (`richDocFor`).
 */
export function checklistDocFor(body, rich) {
    if (!rich)
        return null;
    const doc = parseRichJson(rich);
    if (!doc)
        return null;
    const items = parseChecklist(body);
    if (doc.blocks.length !== items.length)
        return null;
    const passt = doc.blocks.every((block, index) => !block.file && !block.list && blockText(block) === items[index]?.text);
    return passt ? doc : null;
}
/** Das Formatfeld einer Checkliste, wie es abgelegt wird – aufgeräumt, oder gar keins. */
export function normalizeChecklistRich(body, rich) {
    const doc = checklistDocFor(body, rich);
    if (!doc || isPlainDoc(doc))
        return null;
    return serializeDoc(doc);
}
/** Die Einträge einer gespeicherten Checkliste, jeder mit seiner Formatierung. */
export function checklistFromNote(body, rich) {
    const doc = checklistDocFor(body, rich);
    return parseChecklist(body).map((item, index) => {
        const block = doc?.blocks[index];
        return {
            done: item.done,
            value: block ? toRichValue({ blocks: [block] }) : { text: item.text, rich: null },
        };
    });
}
/**
 * Die Einträge zurück in Text und Formatfeld.
 *
 * Leere Einträge fallen weg, und jeder wird vorne und hinten getrimmt – im
 * Text wie in der Formatierung, damit beide nachher zueinander passen.
 */
export function checklistToNote(items) {
    const lines = [];
    const blocks = [];
    for (const item of items) {
        const block = trimBlock(singleLineBlock(docOfValue(item.value)));
        const text = blockText(block);
        if (!text)
            continue;
        lines.push({ text, done: item.done });
        blocks.push(block);
    }
    const body = serializeChecklist(lines);
    if (blocks.length === 0)
        return { body, bodyRich: null };
    const doc = normalizeDoc({ blocks });
    return { body, bodyRich: isPlainDoc(doc) ? null : serializeDoc(doc) };
}
/**
 * Die eigene Reihenfolge der Notizen: angeheftete zuerst, darin und darunter
 * nach `sortOrder`. Bei gleichem Platz entscheidet, was zuletzt bearbeitet
 * wurde – so sehen Notizen, die noch nie jemand verschoben hat, genauso aus
 * wie vor der eigenen Reihenfolge.
 */
export function compareNotesInOwnOrder(a, b) {
    if (a.pinned !== b.pinned)
        return a.pinned ? -1 : 1;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || b.updatedAt.localeCompare(a.updatedAt);
}
/**
 * Einige Notizen neu ordnen, ohne die übrigen zu bewegen.
 *
 * `alle` ist die ganze Reihenfolge, `gewuenscht` die neue Folge eines Teils
 * davon – etwa der Notizen, die eine Suche oder ein Kategorienfilter gerade
 * zeigt. Die gewünschten nehmen die Plätze ein, die sie schon zusammen
 * hatten, nur eben in der neuen Folge; alle anderen bleiben, wo sie sind.
 * Unbekannte Kennungen und Doppelte fallen weg.
 */
export function placeInOrder(alle, gewuenscht) {
    const bekannt = new Set(alle);
    const gesehen = new Set();
    const bewegt = [];
    for (const id of gewuenscht) {
        if (!bekannt.has(id) || gesehen.has(id))
            continue;
        gesehen.add(id);
        bewegt.push(id);
    }
    let naechste = 0;
    return alle.map((id) => (gesehen.has(id) ? bewegt[naechste++] : id));
}
/** Der Abstand zwischen zwei Notizen in der eigenen Reihenfolge – wie bei den Abteilungen. */
export const NOTE_SORT_STEP = 10;
export const noteSchema = z.object({
    id: z.string(),
    title: z.string(),
    body: z.string(),
    /**
     * Die Formatierung des Textes – Fett, Farben, Grössen, Aufzählungen – oder
     * null für reinen Text. Sie steht neben `body` und nicht darin: `body` bleibt
     * lesbarer Text für Suche, Vorschau und jede ältere App (siehe richtext.ts).
     */
    bodyRich: z.string().nullable(),
    kind: noteKindSchema,
    /**
     * In welcher App die Notiz liegt: im Haushalt oder in der DocBase. Dieselbe
     * Trennung wie bei den Dokumenten und aus demselben Grund – ein Merkzettel
     * für den Einkauf hat in einer medizinischen Sammlung nichts verloren.
     */
    bereich: bereichSchema,
    /**
     * Die Schublade, in der die Notiz liegt – null heisst unsortiert.
     *
     * Nur die DocBase fragt danach: Dort steht die Notiz zwischen den Dokumenten
     * und wird über denselben Filter gesucht wie sie. Im Haushalt liegen Notizen
     * in einer eigenen Liste, die nach nichts sortiert wird ausser nach der Zeit.
     */
    categoryId: z.string().nullable(),
    pinned: z.boolean(),
    /**
     * Der Platz in der eigenen Reihenfolge – kleiner heisst weiter oben. Neue
     * Notizen kommen vor alle anderen; verschoben wird über `/api/notes/reihenfolge`.
     * Wer die Liste nach „zuletzt bearbeitet" sortiert, liest das Feld nicht.
     */
    sortOrder: z.number(),
    /**
     * false heisst „nur für mich". Im Haushalt ist eine Notiz zunächst privat
     * und wird bewusst freigegeben – umgekehrt hätte man Geteiltes, das nie
     * jemand teilen wollte.
     *
     * In der DocBase steht hier immer true: Die Sammlung gehört allen, die sie
     * öffnen dürfen (siehe `upsertNoteSchema`).
     */
    shared: z.boolean(),
    color: noteColorSchema,
    createdBy: z.string(),
    updatedBy: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Wie lang Titel und Text einer Notiz sein dürfen. */
export const NOTE_TITLE_MAX = 120;
export const NOTE_BODY_MAX = 20_000;
export const upsertNoteSchema = z
    .object({
    /** Nur beim Anlegen: die Kennung, die das Gerät vergeben hat (`clientIdSchema`). */
    id: clientIdSchema.optional(),
    title: z.string().trim().max(NOTE_TITLE_MAX).default(''),
    body: z.string().max(NOTE_BODY_MAX).default(''),
    /**
     * Fehlt das Feld ganz, kommt die Anfrage von einer App, die noch nichts von
     * Formatierung weiss – dann bleibt, was schon dasteht, solange es zum Text
     * passt (siehe die Route). `null` heisst: bewusst unformatiert.
     */
    bodyRich: z.string().max(500_000).nullable().optional(),
    kind: noteKindSchema.default('text'),
    bereich: bereichSchema.default(DEFAULT_BEREICH),
    categoryId: z.string().max(60).nullable().default(null),
    pinned: z.boolean().default(false),
    shared: z.boolean().default(false),
    color: noteColorSchema.default('default'),
})
    .refine((note) => note.title.trim() !== '' || note.body.trim() !== '', {
    message: 'Titel oder Text ausfüllen',
})
    /**
     * Eine Notiz der DocBase ist immer geteilt.
     *
     * Die Sammlung ist eine gemeinsame: Wer sie öffnen darf, sieht alles darin,
     * und eine Notiz zu einer Studie, die neben der Studie liegt und trotzdem
     * nur einem gehört, beantwortet niemandes Frage. Die Regel steht hier und
     * nicht nur im Fenster der App: Was der Server annimmt, entscheidet, wer
     * eine Notiz später sieht – nicht, welche Knöpfe gerade angezeigt werden.
     *
     * Aus demselben Grund wird hier auch die Formatierung geprüft: Abgelegt wird
     * nur ein Formatfeld, das Zeichen für Zeichen zum Text passt. Bei einer
     * Checkliste heisst das: je Eintrag ein Absatz mit demselben Wortlaut
     * (`checklistDocFor`).
     */
    .transform((note) => ({
    ...note,
    shared: note.bereich === 'docbase' ? true : note.shared,
    bodyRich: note.bodyRich === undefined
        ? undefined
        : note.kind === 'liste'
            ? normalizeChecklistRich(note.body, note.bodyRich)
            : normalizeRich(note.body, note.bodyRich),
}));
/**
 * Die neue Reihenfolge, als Liste von Kennungen – wie bei den Abteilungen.
 *
 * Es darf ein Teil sein: Wer bei gesetztem Filter verschiebt, kennt nur die
 * Notizen, die er sieht. Die übrigen bleiben, wo sie sind (`placeInOrder`).
 */
export const reorderNotesSchema = z.object({
    bereich: bereichSchema.default(DEFAULT_BEREICH),
    ids: z.array(z.string().max(60)).min(1, 'Keine Notizen angegeben').max(5000),
});
/**
 * Wonach eine Notizliste gefragt wird.
 *
 * Der Bereich steht wie bei den Dokumenten zuerst und hat keinen Ausweg: Eine
 * Abfrage, die ihn vergisst, zeigt den Haushalt – nie beides zusammen. Die
 * Kategorie kennt dieselbe Mehrfachauswahl wie dort, samt `unsortiert`.
 */
export const noteQuerySchema = z.object({
    bereich: bereichSchema.default(DEFAULT_BEREICH),
    q: z.string().trim().max(200).optional(),
    categoryId: commaSeparatedIds.optional(),
});
//# sourceMappingURL=household.js.map